"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { getAccessToken } from "@/lib/auth/client";

interface VerificationContextValue {
  /** True = verified, false = unverified, null = loading */
  isVerified: boolean | null;
}

const VerificationContext = createContext<VerificationContextValue | null>(
  null,
);

/**
 * @component VerificationProvider
 * @description Fetches the user's email verification status from /api/auth/session
 * once at the dashboard layout level and makes it available to all descendants.
 * @param children - Dashboard content.
 */
export function VerificationProvider({ children }: { children: ReactNode }) {
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return;

    fetch("/api/auth/session", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled) return;
        if (!json?.success || !json?.data) return;
        const emailVerificationStatus: string = json.data.profile?.emailVerificationStatus ?? '';
        setIsVerified(emailVerificationStatus === 'VERIFIED');
      })
      .catch(() => {
        // Silently fail — banner simply won't show
      });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return (
    <VerificationContext.Provider value={{ isVerified }}>
      {children}
    </VerificationContext.Provider>
  );
}

/**
 * @hook useVerification
 * @description Returns email verification state and helpers.
 * Must be used within a VerificationProvider.
 * @throws If called outside a VerificationProvider.
 */
export function useVerification(): VerificationContextValue {
  const ctx = useContext(VerificationContext);
  if (ctx === null) {
    throw new Error(
      "useVerification must be used within a VerificationProvider",
    );
  }
  return ctx;
}
